import { useGetAllowedUserPermissionsQuery } from "../../../services/adminService";
import RoleListEditor from "./RoleListEditor";
import {
  AddBox as AddBoxIcon,
  Delete as DeleteIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import {
  Autocomplete,
  Avatar,
  Chip,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import * as strings from "common/strings";
import UserDetail from "components/admin/UserDetail";
import IconButton from "components/common/IconButton";
import TextField from "components/common/TextField";
import flags from "flags";
import { useState, Fragment, useEffect, useMemo } from "react";

const CustomKeyInput = ({ onAddKey }) => {
  const [value, setValue] = useState("");
  return (
    <TextField
      hiddenLabel
      placeholder={"Press <Enter> to add"}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          onAddKey(value);
          setValue("");
        }
      }}
    />
  );
};

const OnStateRow = ({
  title,
  component,
  type,
  stateVisibilities,
  onEditStateVisibility,
  noBottomBorder,
}) => {
  return (
    <TableRow>
      <TableCell
        colSpan={5}
        sx={{ borderBottom: noBottomBorder ? "none" : null }}
      >
        <Stack direction={"column"}>
          {[...(stateVisibilities?.[component]?.[type] ?? []), ["", []]].map(
            ([key, values], i) => {
              return (
                <Stack direction={"row"} key={i}>
                  <div
                    style={{
                      height: "50px",
                      minWidth: "120px",
                      textAlign: "center",
                      lineHeight: "50px",
                    }}
                  >
                    {i === 0 && title}
                  </div>
                  <TextField
                    variant={"filled"}
                    size={"small"}
                    value={key}
                    onChange={(e) => {
                      onEditStateVisibility(
                        component,
                        type,
                        i,
                        e.target.value,
                        values
                      );
                    }}
                  />
                  <div
                    style={{
                      height: "50px",
                      minWidth: "120px",
                      textAlign: "center",
                      lineHeight: "50px",
                    }}
                  >
                    {i === 0 && "Values"}
                  </div>
                  <Stack direction={"column"}>
                    {[...(values ?? []), ""].map((value, index1, arr) => {
                      return (
                        <TextField
                          size={"small"}
                          value={value}
                          style={{ paddingBottom: "10px" }}
                          key={index1}
                          onChange={(e) => {
                            onEditStateVisibility(component, type, i, key, [
                              ...(values ?? [])
                                .map((v, index2) =>
                                  index1 === index2 ? e.target.value : v
                                )
                                .filter((v) => v !== null && v.length > 0),
                              ...(index1 === arr.length - 1
                                ? [e.target.value]
                                : []),
                            ]);
                          }}
                        />
                      );
                    })}
                  </Stack>
                </Stack>
              );
            }
          )}
        </Stack>
      </TableCell>
    </TableRow>
  );
};

const UserListEditor = ({ currentUserIds, addUser, removeUser, users }) => {
  const [inputValue, setInputValue] = useState("");

  const options = useMemo(
    () =>
      (users ?? [])
        .filter((u) => u?.enabled && !currentUserIds.includes(u.username))
        .sort((a, b) => (a.email ?? "").localeCompare(b.email ?? "")),
    [users, currentUserIds]
  );

  const selectedOptions = currentUserIds.map(
    (userId) =>
      (users ?? []).find((u) => u.username === userId) ?? {
        username: userId,
        email: userId,
      }
  );

  const handleChange = (_, newValues) => {
    const newUserIds = newValues.map((v) => v.username);
    const added = newUserIds.filter((id) => !currentUserIds.includes(id));
    const removed = currentUserIds.filter((id) => !newUserIds.includes(id));
    added.forEach((id) => addUser(id));
    removed.forEach((id) => removeUser(id));
  };

  return (
    <Autocomplete
      multiple
      size={"small"}
      options={options}
      value={selectedOptions}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={(_, newValue, reason) => {
        if (reason !== "reset") setInputValue(newValue);
      }}
      getOptionLabel={(opt) => opt.email ?? opt.username}
      isOptionEqualToValue={(opt, val) => opt.username === val.username}
      filterOptions={(opts, { inputValue }) => {
        const search = inputValue.toLowerCase();
        return opts.filter(
          (opt) =>
            (opt.email ?? "").toLowerCase().includes(search) ||
            (opt.username ?? "").toLowerCase().includes(search)
        );
      }}
      renderOption={(props, option) => {
        const meta = strings.extractNameFromEmail(option.email);
        const color = strings.stringToColor(option.email);
        return (
          <li {...props} key={option.username}>
            <Stack
              direction={"row"}
              alignItems={"center"}
              gap={1}
              sx={{ pointerEvents: "none" }}
            >
              <Avatar
                sx={{
                  width: "2rem",
                  height: "2rem",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  bgcolor: color,
                }}
              >
                {meta.initials}
              </Avatar>
              <Stack>
                <Typography variant={"body2"}>{meta.name}</Typography>
                <Typography variant={"caption"} color={color}>
                  {option.email}
                </Typography>
                <Typography variant={"caption"} color={"text.secondary"}>
                  {option.username}
                </Typography>
              </Stack>
            </Stack>
          </li>
        );
      }}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => {
          const meta = strings.extractNameFromEmail(option.email);
          const color = strings.stringToColor(option.email);
          return (
            <Tooltip
              key={option.username}
              title={<UserDetail email={option.email} id={option.username} />}
              slotProps={{
                tooltip: {
                  sx: {
                    bgcolor: "background.paper",
                    color: "text.primary",
                    boxShadow: 3,
                    p: 1.5,
                  },
                },
              }}
            >
              <Chip
                {...getTagProps({ index })}
                avatar={
                  <Avatar
                    sx={{ bgcolor: color, fontSize: "0.7rem", fontWeight: 600 }}
                  >
                    {meta.initials}
                  </Avatar>
                }
                label={meta.name}
                size={"small"}
              />
            </Tooltip>
          );
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          hiddenLabel
          size={"small"}
          placeholder={currentUserIds.length === 0 ? "Add users..." : ""}
        />
      )}
      disableCloseOnSelect
      sx={{ minWidth: 200 }}
    />
  );
};

const ComponentVisibilityEditor = ({
  componentVisibilities,
  stateVisibilities,
  visibilityExceptions,
  visibilityUserAccess,
  users,
  setComponentVisibility,
  deleteComponentVisibility,
  setComponentOnStateVisiblity,
  addVisibilityRoleException,
  removeVisibilityRoleException,
  clearVisibilityRoleExceptions,
  addVisibilityUserAccess,
  removeVisibilityUserAccess,
  searchText = "",
}) => {
  const [editing, setEditing] = useState({});
  const knownFlags = Object.fromEntries(flags.visible.map((f) => [f.tag, f]));
  const [allowedRoles, setAllowedRoles] = useState([]);
  const {
    data: permissionsList,
    status: permissionsListStatus,
  } = useGetAllowedUserPermissionsQuery();

  useEffect(() => {
    if (permissionsListStatus === "fulfilled") {
      const allRoles = permissionsList?.permissions ?? [];
      const standardRoles = permissionsList?.defaultPermissions ?? [];
      const allowedRoles = allRoles.filter((r) => !standardRoles.includes(r));
      setAllowedRoles(allowedRoles);
    }
  }, [permissionsList, permissionsListStatus, setAllowedRoles]);

  const getVisibilityExceptionRoles = (component) => {
    return visibilityExceptions[component] ?? [];
  };

  const tags = [
    ...new Set([
      ...flags.visible.map((f) => f.tag),
      ...Object.keys(componentVisibilities),
    ]),
  ].sort((x, y) => {
    if (knownFlags[x] == null && knownFlags[y] == null) {
      return x.localeCompare(y);
    }
    if (knownFlags[x] == null) {
      return 1;
    }
    if (knownFlags[y] == null) {
      return -1;
    }
    const priorityX = knownFlags[x]?.priority ?? 0;
    const priorityY = knownFlags[y]?.priority ?? 0;
    if (priorityX !== priorityY) {
      return priorityY - priorityX;
    }
    return (knownFlags[x].displayName ?? x).localeCompare(
      knownFlags[y].displayName ?? y
    );
  });

  const tagsToDisplay =
    searchText === ""
      ? tags
      : tags.filter((x) => {
          const meta = knownFlags[x];
          return (
            (meta?.displayName ?? "").toLowerCase().includes(searchText) ||
            x.toLowerCase().includes(searchText)
          );
        });

  return (
    <TableContainer>
      <Table size={"small"} className={"flags"}>
        <TableHead>
          <TableRow>
            <TableCell width={"8%"} />
            <TableCell width={"38%"}>{"Component"}</TableCell>
            <TableCell width={"10%"}>{"Is Visible?"}</TableCell>
            <TableCell width={"22%"}>{"Role Exceptions"}</TableCell>
            <TableCell width={"22%"}>{"User Access"}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {tagsToDisplay.map((component) => {
            const value = componentVisibilities[component];
            const controlled = componentVisibilities[component] != null;
            const meta = knownFlags[component];
            return (
              <Fragment key={component}>
                <TableRow sx={meta ? {} : { border: 1, borderColor: "red" }}>
                  <TableCell
                    style={{ borderBottom: editing[component] ? "none" : "" }}
                  >
                    {controlled ? (
                      <Stack direction={"row"}>
                        <IconButton
                          icon={DeleteIcon}
                          onClick={() => deleteComponentVisibility(component)}
                        />
                        {editing[component] ? (
                          <IconButton
                            icon={ExpandLessIcon}
                            onClick={() => {
                              setEditing({ ...editing, [component]: false });
                            }}
                          />
                        ) : (
                          <IconButton
                            icon={ExpandMoreIcon}
                            onClick={() => {
                              setEditing({ ...editing, [component]: true });
                            }}
                          />
                        )}
                      </Stack>
                    ) : (
                      <IconButton
                        icon={AddBoxIcon}
                        onClick={() => setComponentVisibility(component, true)}
                      />
                    )}
                  </TableCell>
                  <Tooltip title={meta?.tooltip ?? ""}>
                    <TableCell
                      style={{ borderBottom: editing[component] ? "none" : "" }}
                    >
                      {meta?.displayName && meta?.displayName !== component ? (
                        <Stack direction={"column"} spacing={0.5}>
                          <Typography
                            variant={"body1"}
                            color={"#000000 !important"}
                          >
                            {meta?.displayName}
                          </Typography>
                          <Typography
                            variant={"body2"}
                            color={"#696D72 !important"}
                          >
                            {component}
                          </Typography>
                        </Stack>
                      ) : (
                        <Typography
                          variant={"body1"}
                          color={"#000000 !important"}
                        >
                          {component}
                        </Typography>
                      )}
                    </TableCell>
                  </Tooltip>
                  <TableCell
                    style={{ borderBottom: editing[component] ? "none" : "" }}
                  >
                    {controlled && (
                      <Switch
                        checked={value}
                        onChange={() =>
                          setComponentVisibility(component, !value)
                        }
                      />
                    )}
                  </TableCell>
                  <TableCell
                    style={{ borderBottom: editing[component] ? "none" : "" }}
                  >
                    {controlled && (
                      <RoleListEditor
                        currentRoles={getVisibilityExceptionRoles(component)}
                        addRole={(role) =>
                          addVisibilityRoleException(component, role)
                        }
                        removeRole={(role) =>
                          removeVisibilityRoleException(component, role)
                        }
                        clearRoles={() =>
                          clearVisibilityRoleExceptions(component)
                        }
                        allowedRoles={allowedRoles}
                      />
                    )}
                  </TableCell>
                  <TableCell
                    style={{ borderBottom: editing[component] ? "none" : "" }}
                  >
                    {controlled && (
                      <UserListEditor
                        currentUserIds={visibilityUserAccess[component] ?? []}
                        addUser={(userId) =>
                          addVisibilityUserAccess(component, userId)
                        }
                        removeUser={(userId) =>
                          removeVisibilityUserAccess(component, userId)
                        }
                        users={users}
                      />
                    )}
                  </TableCell>
                </TableRow>
                {editing[component] ? (
                  <>
                    <OnStateRow
                      title={"Show On State"}
                      component={component}
                      type={"showOnState"}
                      stateVisibilities={stateVisibilities}
                      onEditStateVisibility={setComponentOnStateVisiblity}
                      noBottomBorder={true}
                    />
                    <OnStateRow
                      title={"Hide On State"}
                      component={component}
                      type={"hideOnState"}
                      stateVisibilities={stateVisibilities}
                      onEditStateVisibility={setComponentOnStateVisiblity}
                    />
                  </>
                ) : (
                  <></>
                )}
              </Fragment>
            );
          })}
          <TableRow>
            <TableCell />
            <TableCell>
              <CustomKeyInput
                onAddKey={(key) => setComponentVisibility(key, true)}
              />
            </TableCell>
            <TableCell colSpan={3} />
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ComponentVisibilityEditor;
